/**
 * Canonical user roles. Only these strings are recognized; anything else is ignored when normalizing.
 */
export const CANONICAL_USER_ROLES = [
  "USER",
  "SELLER",
  "PREMIUM_SELLER",
  "STUDENT",
  "ADMIN",
  "PROFESSIONAL",
  "BOOKER",
  "INSTRUCTOR",
] as const;

export type CanonicalUserRole = (typeof CANONICAL_USER_ROLES)[number];

const CANONICAL_SET = new Set<string>(CANONICAL_USER_ROLES);

const LEGACY_ROLE_ALIASES: Record<string, CanonicalUserRole | null> = {
  // `INSTRUCTOR_ADMIN` was the canonical spelling until the rename; `INSTRUCTOR`
  // was its alias. The direction is now inverted, so un-migrated documents keep
  // resolving while `rename-instructor-admin-role-to-instructor` catches up.
  INSTRUCTOR_ADMIN: "INSTRUCTOR",
  CURRICULUM_ADMIN: "INSTRUCTOR",
  CURRICULUM_OFFICER: "INSTRUCTOR",
  CURRICULUM_SELLER: "SELLER",
  BOOKING_USER: "BOOKER",
  BOOKING: "BOOKER",
  BOOKING_CLIENT: "BOOKER",
  BOOKING_PROFESSIONAL: "PROFESSIONAL",
  BOOKING_PROVIDER: "PROFESSIONAL",
  SELL_CURRICULUM: "SELLER",
  BE_BOOKED_AS_DANCE_PROFESSIONAL: "PROFESSIONAL",
  MODERATOR: null,
  PURCHASE_CURRICULUM: null,
  SEARCH_DANCE_PROFESSIONALS: null,
  TAKE_CERTIFICATION_COURSE: null,
  CREATE_JOBS: null,
  BOOK_DANCE_PROFESSIONALS: null,
};

export function normalizeSingleRole(raw: string): CanonicalUserRole | null {
  const normalized = raw.trim().replace(/[\s-]+/g, "_").toUpperCase();
  if (!normalized) return null;
  if (normalized in LEGACY_ROLE_ALIASES) {
    return LEGACY_ROLE_ALIASES[normalized];
  }
  if (!CANONICAL_SET.has(normalized)) return null;
  return normalized as CanonicalUserRole;
}

/**
 * Uppercase, trim, keep only known canonical roles, dedupe.
 */
export function normalizeRolesToCanonical(upperRoles: string[]): CanonicalUserRole[] {
  const out = new Set<CanonicalUserRole>();
  for (const raw of upperRoles) {
    const canonical = normalizeSingleRole(raw);
    if (!canonical) continue;
    out.add(canonical);
  }
  return Array.from(out);
}

/**
 * Roles removed from existing user roles before applying subscription tier bundles
 * (replaced by the tier’s role set; ADMIN / STUDENT / INSTRUCTOR are preserved separately).
 */
export const SUBSCRIPTION_TIER_STRIP_ROLES = new Set<string>([
  "USER",
  "SELLER",
  "PROFESSIONAL",
  "BOOKER",
  "PREMIUM_SELLER",
]);

export function rawRolesCanonicallyIncludeAdmin(upperRoles: string[]): boolean {
  return upperRoles.includes("ADMIN");
}

/** Firestore `array-contains` / `array-contains-any` for operator accounts */
export const STORED_OPERATOR_ADMIN_ROLE_VALUES: readonly string[] = ["ADMIN"];
